import type { TemplateSet } from '@sosocio/creator-types/app';
import type { PageModel } from '@sosocio/creator-types/project';
import { scaleAxisCoordinate } from './geometry-primitives';

export interface PageTemplateLayoutGeometry {
	height: number;
	offset: number;
	width: number;
}

export interface PageTemplateLayoutTarget extends PageTemplateLayoutGeometry {
	template: TemplateSet['templateId'];
}

/**
 * Detaches the complete serializable slot layout from theme/cache records so
 * later template replacement cannot mutate a page's retained slot geometry.
 *
 * @param templateSet Resolved active set to retain with the project page.
 * @returns Independent slot layout suitable for snapshots, history and JSON persistence.
 */
export function clonePageTemplateLayout(templateSet: TemplateSet): TemplateSet {
	return JSON.parse(JSON.stringify(templateSet)) as TemplateSet;
}

/**
 * Reads a retained layout only for an explicit request for the page's current
 * set. New-layout/autofill requests and a changed template must still use the
 * theme resolver rather than accidentally reusing the previous page layout.
 *
 * @param pageModel Page carrying the retained layout and active references.
 * @param requestedSetId Explicit set requested by the caller, if any.
 * @returns Retained active layout when all owning references agree.
 */
export function getPageTemplateLayout(
	pageModel: Pick<PageModel, 'template' | 'templateLayout' | 'templateSetId'>,
	requestedSetId: string | undefined,
): TemplateSet | undefined {
	const layout = pageModel.templateLayout;

	if (
		!layout
		|| requestedSetId !== layout.id
		|| pageModel.templateSetId !== layout.id
		|| String(pageModel.template) !== String(layout.templateId)
	) {
		return undefined;
	}

	return layout;
}

/**
 * Moves the retained full slot rectangles into the same destination page space
 * as page objects. Stable slot ids remain meaningful because their transformed
 * geometry is stored with the page instead of regenerated from theme hashes.
 *
 * @param templateSet Complete source layout captured before changing theme data.
 * @param sourcePage Source trim dimensions and bleed margin.
 * @param targetPage Destination trim dimensions, bleed margin and template id.
 * @returns Detached layout in the destination page space with the original slot ids.
 */
export function scalePageTemplateLayout(
	templateSet: TemplateSet,
	sourcePage: PageTemplateLayoutGeometry,
	targetPage: PageTemplateLayoutTarget,
): TemplateSet {
	const layout = clonePageTemplateLayout(templateSet);
	layout.templateId = targetPage.template;

	if (
		sourcePage.width === targetPage.width
		&& sourcePage.height === targetPage.height
		&& sourcePage.offset === targetPage.offset
	) {
		return layout;
	}

	const scaleHorizontalCoordinate = (value: number): number => scaleAxisCoordinate(
		value,
		sourcePage.width,
		sourcePage.offset,
		targetPage.width,
		targetPage.offset,
	);
	const scaleVerticalCoordinate = (value: number): number => scaleAxisCoordinate(
		value,
		sourcePage.height,
		sourcePage.offset,
		targetPage.height,
		targetPage.offset,
	);
	const scaleFactor = Math.min(
		(targetPage.width + 2 * targetPage.offset) / (sourcePage.width + 2 * sourcePage.offset),
		(targetPage.height + 2 * targetPage.offset) / (sourcePage.height + 2 * sourcePage.offset),
	);

	layout.positions = layout.positions.map((position) => {
		const x = scaleHorizontalCoordinate(position.x);
		const y = scaleVerticalCoordinate(position.y);
		const width = scaleHorizontalCoordinate(position.x + position.width) - x;
		const height = scaleVerticalCoordinate(position.y + position.height) - y;
		const resizedPosition: typeof position = {
			...position,
			x,
			y,
			width,
			height,
			borderwidth: position.borderwidth * scaleFactor,
			overlap_left: position.overlap_left * width / position.width,
			overlap_right: position.overlap_right * width / position.width,
			overlap_top: position.overlap_top * height / position.height,
			overlap_bottom: position.overlap_bottom * height / position.height,
		};

		if (resizedPosition.type === 'text') {
			resizedPosition.pointsize *= scaleFactor;
		}

		return resizedPosition;
	});

	return layout;
}
